/**
 * The navigator's state (1.I-2 deliverable 2), pure: the side peek, the center peek, the second
 * pane, and which of pane 1 and pane 2 is active. `planOpen` answers the next state and, for the
 * modes that leave the page, the address to navigate to.
 *
 * - `peek`: from a peek, that peek shows the target and pushes its history (peeks never nest);
 *   from the page or the split pane the side peek opens on it (or pushes it) and the center peek
 *   closes.
 * - `center`: the side peek closes and the center peek opens with a fresh history.
 * - `full`: from the split pane (or the palette with the split active) pane 2 pushes the target;
 *   otherwise every peek closes and the address is `docHref`.
 * - `split`: pane 2 opens on the target or pushes it and the peeks close, so there are never
 *   more than two panes.
 */
import type { OpenMode } from '@codex/contracts';
import { docHref } from '../editor-services/adapter.js';
import type { NavSource } from './decide-open.js';
import { back, forward, type NavEntry, openStack, type PaneStack, push } from './pane-history.js';

/** What an editor calls to open a document: the contract's `openDoc`, bound to one source. */
export type OpenIn = (
  target: { readonly docId: string; readonly blockId?: string },
  mode: OpenMode,
) => void;

export type PaneName = 'side' | 'center' | 'split';

export interface NavState {
  readonly side: PaneStack | null;
  readonly center: PaneStack | null;
  readonly split: PaneStack | null;
  readonly active: 'main' | 'split';
}

export const INITIAL_NAV: NavState = { side: null, center: null, split: null, active: 'main' };

export interface OpenRequest {
  readonly target: NavEntry;
  readonly mode: OpenMode;
  readonly source: NavSource;
  /** The palette opens in the active pane: with the split active, `full` goes to pane 2. */
  readonly fromPalette?: boolean;
}

export interface OpenPlan {
  readonly state: NavState;
  readonly navigate?: string;
}

function pushed(stack: PaneStack | null, entry: NavEntry): PaneStack {
  return stack ? push(stack, entry) : openStack(entry);
}

export function planOpen(state: NavState, request: OpenRequest): OpenPlan {
  const { target, source } = request;
  switch (request.mode) {
    case 'peek': {
      if (source === 'center' && state.center) {
        return { state: { ...state, center: push(state.center, target) } };
      }
      return {
        state: { ...state, side: pushed(state.side, target), center: null },
      };
    }
    case 'center':
      return { state: { ...state, side: null, center: openStack(target) } };
    case 'split':
      return {
        state: {
          ...state,
          side: null,
          center: null,
          split: pushed(state.split, target),
          active: 'split',
        },
      };
    case 'full': {
      const intoSplit =
        state.split !== null &&
        (source === 'split' || (request.fromPalette === true && state.active === 'split'));
      if (intoSplit) {
        return { state: { ...state, split: pushed(state.split, target), active: 'split' } };
      }
      return {
        state: { ...state, side: null, center: null },
        navigate: docHref(target.docId, target.blockId),
      };
    }
  }
}

/** A route change closes both peeks; the split pane stays. */
export function onLocationChange(state: NavState): NavState {
  if (state.side === null && state.center === null) return state;
  return { ...state, side: null, center: null };
}

export function closePane(state: NavState, which: PaneName): NavState {
  if (state[which] === null) return state;
  const next = { ...state, [which]: null };
  return which === 'split' && state.active === 'split' ? { ...next, active: 'main' } : next;
}

export function stepPane(
  state: NavState,
  which: PaneName,
  direction: 'back' | 'forward',
): NavState {
  const stack = state[which];
  if (!stack) return state;
  const moved = direction === 'back' ? back(stack) : forward(stack);
  return moved === stack ? state : { ...state, [which]: moved };
}

export function setActive(state: NavState, active: 'main' | 'split'): NavState {
  return state.active === active ? state : { ...state, active };
}
